import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useApp } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';

export default function ResetPassword() {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useApp();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (!isAuthenticated) {
      setError('El enlace de recuperación no es válido o expiró. Solicitá uno nuevo.');
      return;
    }

    setIsLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;

      setSuccess(true);
      await logout();
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      console.error('Password update failed:', err);
      setError(err.message || 'No se pudo actualizar la contraseña');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center p-4 bg-[#FAF9F6] relative overflow-hidden">
      <Link
        to="/login"
        className="absolute top-8 left-8 flex items-center gap-2 text-stone-400 hover:text-[#1B2A4E] transition-colors font-medium z-20"
      >
        <SafeIcon name="ArrowLeft" className="w-5 h-5" /> Volver al login
      </Link>

      <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none sol-watermark" style={{ backgroundSize: '800px' }}></div>
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#74ACDF] rounded-full blur-[100px] opacity-20"></div>
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#F6B40E] rounded-full blur-[100px] opacity-10"></div>

      <div className="w-full max-w-md bg-white rounded-[40px] shadow-2xl p-10 border border-stone-100 relative z-10">
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-[#1B2A4E] rounded-2xl flex items-center justify-center text-[#F6B40E] font-bold shadow-xl transform rotate-3 mx-auto mb-6">
            <SafeIcon name="Lock" className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-editorial font-bold text-[#1B2A4E]">Nueva contraseña</h1>
          <p className="label-technical text-stone-400 mt-3">Elegí una contraseña segura</p>
        </div>

        {success ? (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <SafeIcon name="Check" className="w-8 h-8 text-green-500" />
            </div>
            <p className="text-stone-500 text-base leading-relaxed">
              Tu contraseña fue actualizada. Redirigiendo al login...
            </p>
            <SafeIcon name="Loader" className="w-6 h-6 text-[#74ACDF] animate-spin mx-auto" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {!isAuthenticated && (
              <p className="text-amber-700 text-sm font-medium bg-amber-50 rounded-xl px-4 py-3">
                Abrí el enlace que te enviamos por email para continuar. Si expiró,{' '}
                <Link to="/recuperar-password" className="underline font-bold">
                  solicitá uno nuevo
                </Link>
                .
              </p>
            )}

            <div>
              <label className="block label-technical text-stone-400 mb-2">Nueva contraseña</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-5 py-4 rounded-2xl border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 bg-stone-50 focus:bg-white outline-none transition-all text-[#1B2A4E]"
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="block label-technical text-stone-400 mb-2">Confirmar contraseña</label>
              <input
                type="password"
                required
                minLength={6}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-5 py-4 rounded-2xl border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 bg-stone-50 focus:bg-white outline-none transition-all text-[#1B2A4E]"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p className="text-red-600 text-sm font-medium bg-red-50 rounded-xl px-4 py-3">{error}</p>
            )}

            <button
              type="submit"
              disabled={isLoading || !isAuthenticated}
              className="w-full bg-[#1B2A4E] hover:bg-[#253969] disabled:opacity-50 disabled:hover:bg-[#1B2A4E] text-white font-bold py-4 rounded-2xl transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3 mt-8"
            >
              {isLoading ? (
                <SafeIcon name="Loader" className="animate-spin w-6 h-6" />
              ) : (
                'Actualizar contraseña'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
